import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ColumnPipe } from '@sharedModule/pipe/column/column.pipe';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { TRANSACTION_SETTINGS } from '../../constant/transaction-settings.constant';
import { TransactionSettingsService } from '../../services/transaction-settings.service';
import {
  MasterPartnerTransactionSettingsInterface,
  IndividualTransactionSettingsInterface,
} from '../../types/transaction-settings';
import { UpsertMasterPartnerTransactionSettingsComponent } from '../upsert-master-partner-transaction-settings/upsert-master-partner-transaction-settings.component';

@Component({
  selector: 'app-master-partner-transaction-settings',
  standalone: true,
  imports: [
    ColumnPipe,
    InputModule,
    CommonModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './master-partner-transaction-settings.component.html',
  styleUrl: './master-partner-transaction-settings.component.scss',
})
export class MasterPartnerTransactionSettingsComponent
  implements OnInit, OnDestroy
{
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.transaction;
  public readonly tabs = TRANSACTION_SETTINGS;

  currentTabLabel: string = this.tabs[0].code;

  displayedColumns: string[] = ['name', 'action'];
  dataSource!: MatTableDataSource<MasterPartnerTransactionSettingsInterface>;
  transactionSettings!: MasterPartnerTransactionSettingsInterface;
  filterForm!: FormGroup;

  partnerList$!: Observable<PartnerListInterface[]>;
  #destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialog,
    private partnerService: PartnerService,
    private messageHandlerService: MessageHandlerService,
    private confirmationDialogService: ConfirmationDialogService,
    private transactionSettingsService: TransactionSettingsService
  ) {
    this.partnerList$ = this.partnerService.sharedData$;
  }

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      [this.attrConstant.partner.key]: ['', Validators.required],
    });
  }

  getMasterPartnerTransactionSettings(): void {
    const formValue = this.filterForm.value;
    this.transactionSettingsService
      .getMasterPartnerTransactionSettings({
        partner_id: formValue[this.attrConstant.partner.key],
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success && x.response) {
            this.transactionSettings = x.response;
            this.dataSource = new MatTableDataSource(
              x.response[this.currentTabLabel] || []
            );
          } else {
            this.messageHandlerService.show(
              'Failed to load transaction settings'
            );
            this.dataSource =
              new MatTableDataSource<MasterPartnerTransactionSettingsInterface>(
                []
              );
          }
        },
      });
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.currentTabLabel = this.tabs[event.index].code;
    this.dataSource = new MatTableDataSource(
      this.transactionSettings[this.currentTabLabel] || []
    );
  }

  addTransactionSettings(): void {
    const dialogRef = this.dialogRef.open(
      UpsertMasterPartnerTransactionSettingsComponent,
      {
        disableClose: true,
        data: {
          action: 'Add',
          column: this.currentTabLabel,
        },
      }
    );
    dialogRef.afterClosed().subscribe(result => {
      if (result.success) this.handelSuccessResponse(result.partnerId);
    });
  }

  onEdit(): void {
    const dialogRef = this.dialogRef.open(
      UpsertMasterPartnerTransactionSettingsComponent,
      {
        disableClose: true,
        data: {
          isUpdate: true,
          partner_id: this.filterForm.value[this.attrConstant.partner.key],
          id: this.transactionSettings.id,
          column: this.currentTabLabel,
          code: this.transactionSettings[this.currentTabLabel].map(
            (item: { code: number }) => item.code
          ),
        },
      }
    );
    dialogRef.afterClosed().subscribe(result => {
      if (result.success) this.handelSuccessResponse(result.partnerId);
    });
  }

  handelSuccessResponse(partnerId: number): void {
    this.filterForm.patchValue({
      [this.attrConstant.partner.key]: partnerId,
    });
    this.getMasterPartnerTransactionSettings();
  }

  async onDelete(data: IndividualTransactionSettingsInterface): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.transactionSettingsService
        .deleteMasterPartnerTransactionSetting({
          partner_id: this.filterForm.value[this.attrConstant.partner.key],
          id: this.transactionSettings.id,
          column: this.currentTabLabel,
          code: data['code'],
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) this.getMasterPartnerTransactionSettings();
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
